import React, { useState, useEffect } from 'react';
import Hero from '../components/Hero';
import BookRow from '../components/BookRow';
import AdBanner from '../components/AdBanner';
import TrendingSection from '../components/TrendingSection';
import RecentReviewsRow from '../components/RecentReviewsRow';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Upload, Star } from 'lucide-react';

// Use current origin for deployed environment, fallback to env var for development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [books, setBooks] = useState([]);
  const [featuredBook, setFeaturedBook] = useState(null);
  const [recommendedBooks, setRecommendedBooks] = useState([]);
  const [continueReadingBooks, setContinueReadingBooks] = useState([]);
  const [viewCounts, setViewCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBooks();
    fetchFeaturedBook();
    fetchRecommendedBooks();
    fetchViewCounts();
    fetchContinueReading();

    // Auto-refresh view counts every 30s to keep them live
    const viewPollInterval = setInterval(fetchViewCounts, 30000);

    // Re-fetch everything when the Home nav button is clicked (2026-06-08).
    // Mirrors the same handler in LandingPage so an authenticated user who
    // clicks "Home" while already on `/` sees a freshly refetched dashboard.
    const handleHomeReset = () => {
      fetchBooks();
      fetchFeaturedBook();
      fetchRecommendedBooks();
      fetchViewCounts();
      fetchContinueReading();
    };
    window.addEventListener('scb:home-reset', handleHomeReset);

    return () => {
      clearInterval(viewPollInterval);
      window.removeEventListener('scb:home-reset', handleHomeReset);
    };
  }, []);

  const fetchViewCounts = async () => {
    try {
      const response = await axios.get(`${API}/book-views-summary`);
      setViewCounts(response.data);
    } catch (error) {
      console.warn('[Home] view counts unavailable:', error?.message || error);
    }
  };

  const fetchBooks = async () => {
    try {
      // Force no-cache with multiple methods
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/books?_=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log('Books fetched from API:', response.data);
      setBooks(response.data);
      
      // If no books, make sure we show empty state
      if (response.data.length === 0) {
        console.log('No books in database - showing empty state');
      }
    } catch (error) {
      console.error('Failed to fetch books:', error);
      setBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedBook = async () => {
    try {
      const response = await axios.get(`${API}/featured-book`);
      if (response.data && response.data.id) {
        setFeaturedBook(response.data);
      }
    } catch (error) {
      console.log('No featured book set, will use first book as fallback');
    }
  };

  const fetchRecommendedBooks = async () => {
    try {
      const response = await axios.get(`${API}/recommendations`);
      if (response.data && response.data.length > 0) {
        setRecommendedBooks(response.data);
      }
    } catch (error) {
      console.log('No recommendations set');
    }
  };

  const fetchContinueReading = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/user/continue-reading`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.length > 0) {
        setContinueReadingBooks(response.data);
      }
    } catch (error) {
      console.log('No continue reading data');
    }
  };

  // Organize books by genre
  const organizeByGenre = () => {
    const categories = {};

    books.forEach(book => {
      if (book.genres && book.genres.length > 0) {
        // Dedupe genres within a single book to avoid pushing the same book
        // into the same genre bucket twice (which causes duplicate React keys).
        const uniqueGenres = Array.from(new Set(book.genres));
        uniqueGenres.forEach(genre => {
          if (!categories[genre]) {
            categories[genre] = [];
          }
          categories[genre].push(book);
        });
      }
    });

    return Object.entries(categories).map(([genre, genreBooks]) => ({
      id: genre.toLowerCase(),
      title: genre,
      books: genreBooks,
    }));
  };

  const bookCategories = books.length > 0 ? organizeByGenre() : [];
  // Use the featured book from settings, or fallback to first book
  const displayFeaturedBook = featuredBook || (books.length > 0 ? books[0] : null);
  
  // All books section (shows every book)
  const allBooksCategory = books.length > 0 ? {
    id: 'all-books',
    title: 'All Books',
    books: books
  } : null;

  // Continue Reading section (shows books user has progress on)
  const continueReadingCategory = continueReadingBooks.length > 0 ? {
    id: 'continue-reading',
    title: 'Continue Reading',
    books: continueReadingBooks
  } : null;

  // Audiobooks section (filter only audiobooks)
  const audiobooksCategory = books.filter(book => book.is_audiobook || book.audiobook_chapters?.length > 0).length > 0 ? {
    id: 'audiobooks',
    title: 'Audiobooks',
    books: books.filter(book => book.is_audiobook || book.audiobook_chapters?.length > 0)
  } : null;

  // Recommended books section
  const recommendedCategory = recommendedBooks.length > 0 ? {
    id: 'recommended',
    title: '⭐ Staff Picks',
    books: recommendedBooks
  } : null;

  // Regular books (non-audiobooks)
  const regularBooks = books.filter(book => !book.is_audiobook && !book.audiobook_chapters?.length);

  return (
    <div className="bg-black min-h-screen flex flex-col">
      {/* Hero Section */}
      <section className="flex-shrink-0">
        {displayFeaturedBook ? (
          <Hero book={displayFeaturedBook} />
        ) : (
          <div className="h-[70vh] w-full flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900">
            <div className="text-center space-y-6 px-4">
              <h1 className="text-5xl font-bold text-white">Welcome to Seven Chapter Books</h1>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Your premium book reading platform
              </p>
              {userRole === 'admin' && (
                <div className="space-y-4">
                  <p className="text-gray-300">No books uploaded yet. Start by uploading your first book!</p>
                  <Button
                    size="lg"
                    onClick={() => navigate('/upload')}
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-6 text-lg"
                  >
                    <Upload className="mr-2 h-6 w-6" />
                    Upload Your First Book
                  </Button>
                </div>
              )}
              {userRole !== 'admin' && (
                <p className="text-gray-400">Check back soon for amazing books!</p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Trending Now Section */}
      <TrendingSection />

      {/* Book Rows Section - Completely separate from hero */}
      {books.length > 0 && (
        <section className="relative bg-black pt-8 pb-20 space-y-12">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
              <p className="text-white mt-4">Loading books...</p>
            </div>
          ) : (
            <>
              {/* Staff Picks / Recommended Section - Show first if available */}
              {recommendedCategory && (
                <BookRow title={recommendedCategory.title} books={recommendedCategory.books} viewCounts={viewCounts} />
              )}

              {/* Recently Added Section - Show newest books first */}
              {continueReadingCategory && (
                <BookRow title={continueReadingCategory.title} books={continueReadingCategory.books} viewCounts={viewCounts} />
              )}

              {/* Audiobooks Section - Separate section for audiobooks */}
              {audiobooksCategory && (
                <BookRow title={audiobooksCategory.title} books={audiobooksCategory.books} isAudiobookRow={true} viewCounts={viewCounts} />
              )}

              {/* All Books Section */}
              {allBooksCategory && (
                <BookRow title={allBooksCategory.title} books={allBooksCategory.books} viewCounts={viewCounts} />
              )}
              
              {/* Genre-based categories */}
              {bookCategories.map((category, index) => (
                <React.Fragment key={category.id}>
                  <BookRow title={category.title} books={category.books} viewCounts={viewCounts} />
                  
                  {/* Insert Ad Banner after 2nd category */}
                  {index === 1 && <AdBanner />}
                </React.Fragment>
              ))}
            </>
          )}
        </section>
      )}

      {/* Recent Community Reviews — admins see a trash overlay on each card */}
      <RecentReviewsRow limit={6} dense />
    </div>
  );
};

export default Home;